import { z } from 'zod';

/**
 * Whether a guardian link is still standing.
 *
 * Two states, not a machine — the shape `MATCH_STATUSES` argues for, and for the
 * same reason: a link begins by agreement (a code, offered and redeemed) and
 * stops for a reason, and nothing in between is a state anybody acts on
 * differently. **Why** it stopped is {@link GuardianLinkEndReasonSchema}, kept
 * as its own field so "I removed my wali" and "his account was closed" stay
 * distinguishable without the status growing a value per cause.
 *
 * Only `active` grants a guardian anything. P24b's read paths ask this question
 * on **every request** rather than caching the answer — see the end-reason
 * docblock for why that is the design and not an oversight.
 */
export const GUARDIAN_LINK_STATUSES = ['active', 'ended'] as const;

export const GuardianLinkStatusSchema = z.enum(GUARDIAN_LINK_STATUSES);

export type GuardianLinkStatus = (typeof GUARDIAN_LINK_STATUSES)[number];

/** A link exists because a code was redeemed; there is no draft. */
export const INITIAL_GUARDIAN_LINK_STATUS: GuardianLinkStatus = 'active';

/**
 * The status "my guardians" and "my wards" mean.
 *
 * **A second constant holding the same value as
 * {@link INITIAL_GUARDIAN_LINK_STATUS}, and they must not be merged** — the
 * discipline `LIVE_MATCH_STATUS` keeps for the identical reason. One says what a
 * link is created as, the other says which links are shown and which grant
 * access. They coincide today; the day a third status arrives ahead of `active`,
 * merging them would silently point the access check at the creation state.
 */
export const LIVE_GUARDIAN_LINK_STATUS: GuardianLinkStatus = 'active';

/**
 * The link is still standing.
 *
 * The **partial unique index** enforcing "one live link per (ward, guardian)" is
 * built from exactly this predicate, so the index and this function are one fact
 * with two spellings — the discipline `isActiveMatchStatus` keeps. A status
 * counted live here and omitted there would let a pair accumulate links, and a
 * revoked guardian could be re-linked into a second row that nothing revoked.
 */
export function isActiveGuardianLinkStatus(status: GuardianLinkStatus): boolean {
  return status === 'active';
}

/**
 * Why a link stopped.
 *
 * Four reasons rather than a boolean, because the ward and the guardian are owed
 * different sentences and the audit trail is owed the truth about who acted:
 *
 * - `revoked_by_ward` — she removed him. The act this whole phase is measured on.
 * - `left_by_guardian` — he stepped down. A wali must be able to hand the duty
 *   back; a relationship only one party can end is a trap for the other.
 * - `ward_account_closed` / `guardian_account_closed` — a ban, or a deletion.
 *   Written by a listener, never by a person.
 *
 * **None of these is a delay.** Ending a link is a write to this row, and the
 * access check re-reads the row per request, so the guardian's next read is
 * refused whatever his session still holds. That is deliberate and it is the
 * lesson P19a and P19d both cost a phase to learn: a revocation that has to
 * reach a *cache* before it takes effect is a revocation that had not happened
 * yet, and every unit spec passes while it has not.
 */
export const GUARDIAN_LINK_END_REASONS = [
  'revoked_by_ward',
  'left_by_guardian',
  'ward_account_closed',
  'guardian_account_closed',
] as const;

export const GuardianLinkEndReasonSchema = z.enum(GUARDIAN_LINK_END_REASONS);

export type GuardianLinkEndReason = (typeof GUARDIAN_LINK_END_REASONS)[number];
